// Plain-language follow-up reminders derived from the event log (data gaps and
// overdue items). Shared by the Shell notification banner and the Dashboard's
// "Smart reminders" widget so both surface the exact same nudges.

import { todayInTimezone, type MedicalEvent } from "@medassist/shared";

/** Returns follow-up messages for events that look stale or overdue. */
export function getNotifications(events: MedicalEvent[]): string[] {
  const notes: string[] = [];
  const now = Date.now();
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  // Today's local calendar date for comparing CalendarDate fields without tz shift.
  const todayLocal = todayInTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);

  for (const e of events) {
    const p = e.payload as Record<string, unknown>;
    if (e.eventType === "stress_event" && p.ongoing === true) {
      const age = now - new Date(e.occurredAt).getTime();
      if (age > SEVEN_DAYS_MS) {
        notes.push(`Ongoing stress event recorded ${Math.floor(age / 86_400_000)}d ago — still ongoing?`);
      }
    }
    if (e.eventType === "hospitalization" && !p.dischargedAt) {
      notes.push(`Hospitalization at ${p.facility ?? "unknown facility"} has no discharge date — still admitted?`);
    }
    if (e.eventType === "visit" && p.followUpDate) {
      // followUpDate is a CalendarDate — compare strings to avoid UTC-midnight tz shift.
      const dateStr = String(p.followUpDate).slice(0, 10);
      if (dateStr < todayLocal) {
        const daysAgo = Math.floor((now - new Date(dateStr).getTime()) / 86_400_000);
        notes.push(`Follow-up visit was due ${daysAgo}d ago (${dateStr})`);
      }
    }
    if (e.eventType === "vaccination" && p.nextDoseDue) {
      // nextDoseDue is a CalendarDate — compare strings.
      const dateStr = String(p.nextDoseDue).slice(0, 10);
      if (dateStr < todayLocal) {
        const daysAgo = Math.floor((now - new Date(dateStr).getTime()) / 86_400_000);
        notes.push(`Vaccination dose due ${daysAgo}d ago: ${p.name ?? "unknown vaccine"} (${dateStr})`);
      }
    }
  }
  return notes;
}
